/**
 * The one place that speaks both languages.
 *
 * Every screen in `../pages` and every component in `../components` was
 * written against the shapes in `../data/types` — `Task`, `Client`,
 * `TeamMember`, `ContentItem`. The server speaks in cycles, pieces,
 * commitments and stages. This file is the whole of the translation, and it is
 * deliberately one file: when a label on screen disagrees with a column in the
 * database, there is exactly one place to look.
 *
 * The design repo's version of this file fetched nine endpoints and kept the
 * results in nine `useState`s. That shape is kept, because it is the shape the
 * screens read. What it fetches is different: five real reads against this
 * platform, four of the nine derived from them.
 *
 * Nothing here decides what anybody may see. The server removes the retainer
 * from a client record before it leaves the process, and a `monthlyRetainer`
 * that is absent here is absent because it never arrived — not because a
 * component chose not to render it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { api, ApiError } from '../lib/api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { cheer, DID } from '../lib/hapiMood';
import { PhotoContext } from './PhotoContext';
import { useAuth, personColor } from './AuthContext';
import type {
  ActivityLogEntry,
  ApprovalStatus,
  Client,
  ContentItem,
  ContentPlatform,
  ContentType,
  Deliverable,
  Draft,
  Notification,
  Priority,
  Project,
  SimpleStatus,
  Task,
  TaskNote,
  TaskStatus,
  TeamMember,
  WeeklyTimeEntry
} from '../data/types';
import type {
  Assignment,
  AssignmentRole,
  PlanLine,
  BoardView,
  Client as WireClient,
  MonthView,
  Person,
  Stage,
  TodayView,
  Workload
} from '../api';
import { initials as deriveInitials, isoDate, monthOf, relativeTime, today } from '../lib/format';

/* ------------------------------------------------------------------ *
 * The vocabulary, in both directions.
 * ------------------------------------------------------------------ */

const STAGE_TO_STATUS: Record<Stage, TaskStatus> = {
  briefed: 'Briefed',
  shot: 'Shot',
  editing: 'Editing',
  internal_review: 'Review',
  with_client: 'With client',
  scheduled: 'Scheduled',
  posted: 'Posted'
};

export const STATUS_TO_STAGE: Record<TaskStatus, Stage> = {
  Briefed: 'briefed',
  Shot: 'shot',
  Editing: 'editing',
  Review: 'internal_review',
  'With client': 'with_client',
  Scheduled: 'scheduled',
  Posted: 'posted'
};

const PRIORITY_TO_LABEL: Record<string, Priority> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent'
};

export const LABEL_TO_PRIORITY: Record<Priority, string> = {
  Low: 'low',
  Medium: 'medium',
  High: 'high',
  Urgent: 'urgent'
};

const PLATFORM_TO_LABEL: Record<string, ContentPlatform> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  x: 'X',
  other: 'Other'
};

export const LABEL_TO_PLATFORM: Record<string, string> = Object.fromEntries(
  Object.entries(PLATFORM_TO_LABEL).map(([wire, label]) => [label, wire])
);

const KIND_TO_LABEL: Record<string, ContentType> = {
  reel: 'Reel',
  carousel: 'Carousel',
  post: 'Static Post',
  story: 'Story',
  article: 'Article',
  other: 'Video'
};

/**
 * The reverse of `KIND_TO_LABEL`, for the create form.
 *
 * Written out rather than derived, because the forward map is not injective:
 * the wire has one `other` and the design's vocabulary has both 'Video' and
 * 'Other' landing on it. Reversing it programmatically would give whichever
 * of the two happened to come last in the object, and the answer would change
 * if somebody reordered the lines.
 */
export const LABEL_TO_KIND: Record<ContentType, string> = {
  Reel: 'reel',
  Carousel: 'carousel',
  'Static Post': 'post',
  Story: 'story',
  Article: 'article',
  Video: 'other'
};

const STEP_TO_LABEL: Record<string, SimpleStatus> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  done: 'Done'
};

export const LABEL_TO_STEP: Record<SimpleStatus, string> = {
  'Not Started': 'not_started',
  'In Progress': 'in_progress',
  Done: 'done'
};

const APPROVAL_TO_LABEL: Record<string, ApprovalStatus> = {
  pending: 'Pending',
  approved: 'Approved',
  changes_requested: 'Changes Requested'
};

export const LABEL_TO_APPROVAL: Record<ApprovalStatus, string> = {
  Pending: 'pending',
  Approved: 'approved',
  'Changes Requested': 'changes_requested'
};

/** Where a photo is served. The version changes when the photo does, so the browser keeps it. */
function photoUrl(personId: string, at: string): string {
  return `/api/photos/${encodeURIComponent(personId)}?v=${encodeURIComponent(at)}`;
}

const ACCOUNT_STATUS: Record<string, Client['accountStatus']> = {
  active: 'Active',
  prospect: 'Prospect',
  unconfirmed: 'Unconfirmed',
  former: 'Former',
  // A prospect who said no. Without this they fell through to Active.
  lost: 'Lost'
};

/* ------------------------------------------------------------------ *
 * The wire shapes this file reads that `../api` does not yet name.
 * ------------------------------------------------------------------ */

interface PlannedPiece {
  piece_id: string;
  cycle_id: string;
  cycle_month?: string;
  month_pinned?: boolean;
  commitment_id?: string | null;
  commitment?: string | null;
  client_id: string;
  client_name: string;
  avatar_color: string;
  title: string;
  kind: string;
  stage: Stage;
  platform: string | null;
  topic: string | null;
  hook: string | null;
  caption: string | null;
  cta: string | null;
  reel_link: string | null;
  notes?: string | null;
  owner_id: string | null;
  owner_name: string | null;
  scheduled_for: string | null;
  due: string | null;
  posted_at: string | null;
  design_status: string;
  editing_status: string;
  approval_status: string;
}

/** One thing a client is owed in the month on screen, and how far along it is. */
export interface Requirement {
  client_id: string;
  client_name: string;
  avatar_color: string;
  /** Null when the month is not open yet and this is the standing plan. */
  commitment_id: string | null;
  metric: string;
  unit: string;
  target_count: number;
  /** Pieces counting towards it, at any stage. */
  planned: number;
  posted: number;
  /** When this client's month was closed, or null while it is still open. */
  closed_at?: string | null;
}

interface ContentPlan {
  month: string;
  pieces: PlannedPiece[];
  requirements?: Requirement[];
  undated: number;
}

interface AuditRow {
  id: string;
  at: string;
  actor_kind: string;
  actor_id: string | null;
  action: string;
  subject_type: string;
  subject_id: string | null;
}

export interface DirectoryEntry {
  id: string;
  name: string;
  title: string;
  color: string;
  department: string;
}

/** What closing a month did with it. */
export interface Settled {
  delivered: number;
  carried: number;
  missed: number;
}

/** A piece that changed months because its post date moved, `YYYY-MM-01` both. */
export interface Moved {
  from: string;
  to: string;
}

interface AppDataContextValue {
  loading: boolean;
  /** What went wrong on the last load, or null. Kept so a screen can say so. */
  error: string | null;
  /** The month everything on screen is about, as `YYYY-MM-01`. */
  month: string;
  setMonth: (month: string) => void;
  reload: () => void;

  tasks: Task[];
  setTaskStatus: (id: string, status: TaskStatus) => Promise<void>;
  updateTask: (id: string, patch: Draft<Task>) => Promise<void>;
  addTask: (task: Draft<Task> & { clientId: string }) => Promise<void>;
  /** Fetch the thread under a piece. Tasks carry an empty one until this runs. */
  loadTaskNotes: (id: string) => Promise<void>;
  addTaskNote: (id: string, text: string) => Promise<void>;

  content: ContentItem[];
  /**
   * What each client is owed in the month on screen, with how many pieces
   * count towards each line and how many have posted — the same numbers the
   * client page shows.
   */
  requirements: Requirement[];
  /**
   * Change a piece. Answers with the months it moved between when the new post
   * date pulled it into another one, so the page can say so and offer to put
   * it back; null when it stayed where it was, which is the usual answer.
   */
  updateContent: (id: string, patch: Draft<ContentItem>) => Promise<Moved | null>;
  /** Move a piece to another month, `YYYY-MM-01`. */
  refileContent: (id: string, month: string) => Promise<void>;
  /**
   * Settle a client's month. `carry` moves what has not posted into the next
   * one; without it the unfinished work stays where it is, on the record as a
   * month the studio did not deliver. Answers with what it did.
   */
  closeMonth: (clientId: string, month: string, carry: boolean) => Promise<Settled>;
  /** Open a closed month again. It does not bring carried work back. */
  reopenMonth: (clientId: string, month: string) => Promise<void>;
  /** Resolves to the new piece's id. */
  addContent: (item: Draft<ContentItem> & { clientId: string }) => Promise<string>;
  /** Delete a piece. It is kept, so `restoreContent` can put it back. */
  removeContent: (id: string) => Promise<void>;
  restoreContent: (id: string) => Promise<void>;

  deliverables: Deliverable[];
  toggleDeliverable: (id: string) => Promise<void>;

  /** Everybody still here. */
  team: TeamMember[];
  /** People who have left: out of every picker, kept so their past work keeps a name. */
  archivedTeam: TeamMember[];
  archivePerson: (id: string) => Promise<void>;
  restorePerson: (id: string) => Promise<void>;
  directory: DirectoryEntry[];
  /** A photo URL for a person, by id or by name, when they have one. */
  photoFor: (idOrName: string) => string | undefined;
  /** Change your own name or title. */
  updateMe: (patch: { name?: string; title?: string }) => Promise<void>;
  /** A data URL, already resized. */
  setPhoto: (personId: string, dataUrl: string) => Promise<void>;
  clearPhoto: (personId: string) => Promise<void>;

  /** Who is on which client, and how. */
  assignments: Assignment[];
  assignPerson: (clientId: string, personId: string, role: AssignmentRole) => Promise<void>;
  unassignPerson: (clientId: string, personId: string) => Promise<void>;
  /** A client's standing monthly plan. Fetched when asked for. */
  loadPlan: (clientId: string) => Promise<PlanLine[]>;
  savePlan: (
    clientId: string,
    items: Array<{ metric: string; targetCount: number; unit: string }>
  ) => Promise<PlanLine[]>;

  /**
   * Clients the studio is working with or pitching: what every picker offers.
   * Former and lost clients are left out so nobody files new work against
   * them; their past work still finds them by id through `clients`.
   */
  currentClients: Client[];

  /** Everyone not archived. What every picker and roll-up should use. */
  clients: Client[];
  /** Put away, with their history intact, and restorable. */
  archivedClients: Client[];
  archiveClient: (id: string) => Promise<void>;
  /** Back from the archive; a former client comes back as active. */
  restoreClient: (id: string) => Promise<void>;
  addClient: (client: Draft<Client>) => Promise<void>;
  updateClient: (id: string, patch: Draft<Client>) => Promise<void>;
  projects: Project[];

  activityLog: ActivityLogEntry[];
  weeklyTime: WeeklyTimeEntry[];

  today: TodayView | null;

  /**
   * The month roll-up exactly as the server sent it.
   *
   * Exposed raw, alongside the translated `deliverables`, because the money is
   * the one thing that must not be reshaped on the way past: `money` is absent
   * — not empty — for anybody who is not a partner, and the retainer figures
   * are strings because they are Postgres `numeric` and turning money into a
   * float on the way to a screen is how a rounding error reaches an invoice.
   */
  monthView: MonthView | null;
  /** Posted against promised, for the last six months, oldest first. */
  history: HistoryMonth[];

  notifications: Notification[];
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
}

const AppDataContext = createContext<AppDataContextValue | null>(null);

/**
 * Which notifications this browser has already been shown.
 *
 * Notifications here are derived from state, not stored as rows: "three things
 * are overdue" is a fact about the pieces table, and writing it into a
 * notifications table would create a second copy that goes stale the moment
 * somebody fixes the thing. The consequence is that "read" has nowhere on the
 * server to live, so it lives in this browser. Dismissing on a phone does not
 * dismiss on a laptop, which is the honest trade for never showing a
 * notification about work that is already done.
 */
const READ_KEY = 'hapi.notifications-read';

function readDismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(READ_KEY);
    return new Set(raw === null ? [] : (JSON.parse(raw) as string[]));
  } catch {
    // A private window, cleared site data, or a browser set to block storage.
    // Everything unread is a fine answer; a crash is not.
    return new Set();
  }
}

function writeDismissed(ids: Set<string>): void {
  try {
    window.localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
  } catch {
    /* see readDismissed */
  }
}

/**
 * How many client accounts one person can follow up on.
 *
 * The same line the server draws in `src/db/people.ts`, repeated here rather
 * than fetched because it decides how one number on one card is coloured, and
 * a round trip for a constant is not worth the drift it saves.
 */
const MAX_CLIENTS = 10;

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState(() => monthOf(today()));
  const [nonce, setNonce] = useState(0);

  const [board, setBoard] = useState<BoardView | null>(null);
  const [plan, setPlan] = useState<ContentPlan | null>(null);
  const [wireClients, setWireClients] = useState<WireClient[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loads, setLoads] = useState<Workload[]>([]);
  const [monthView, setMonthView] = useState<MonthView | null>(null);
  const [todayView, setTodayView] = useState<TodayView | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [history, setHistory] = useState<HistoryMonth[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(readDismissed);
  // Piece threads, fetched when a drawer opens rather than with the board:
  // the board is every piece in the studio, and nobody reads every thread.
  const [pieceNotes, setPieceNotes] = useState<Record<string, TaskNote[]>>({});
  /**
   * The question the sign-off gate asks, and the answer it is waiting for.
   *
   * Held here rather than on a page because all three ways of marking a piece
   * posted go through this file, and a dialog per page would be three copies
   * of the same sentence drifting apart.
   */
  const [gate, setGate] = useState<{ title: string; question: string; answer: (ok: boolean) => void } | null>(
    null
  );

  // Saves come in bursts — a row of cells in write mode, a drag across the
  // board — and each used to refetch all ten reads on its own. Waiting a beat
  // folds a burst into one refresh.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reload = useCallback(() => {
    if (reloadTimer.current !== null) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => {
      reloadTimer.current = null;
      setNonce((n) => n + 1);
    }, 250);
  }, []);
  useEffect(
    () => () => {
      if (reloadTimer.current !== null) clearTimeout(reloadTimer.current);
    },
    []
  );
  // Which fetch is a refresh of what is already on screen. A refresh keeps
  // showing the current data while it runs; only a new month or a new
  // sign-in is "loading", or every save would flash every page to a spinner.
  const shown = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setBoard(null);
      setPlan(null);
      setWireClients([]);
      setPeople([]);
      setLoads([]);
      setMonthView(null);
      setTodayView(null);
      setAudit([]);
      setHistory([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const view = `${currentUser?.id ?? ''}|${month}`;
    if (shown.current !== view) setLoading(true);

    // `allSettled`, not `all`. The audit log is partner-only and the month
    // roll-up can be empty; one 403 on a secondary read should not blank every
    // screen in the platform for somebody who is allowed to see the board.
    void Promise.allSettled([
      api.get<BoardView>('/board'),
      api.get<ContentPlan>('/content', { month }),
      api.get<WireClient[]>('/clients', { includeArchived: true }),
      api.get<Person[]>('/people', { includeInactive: true }),
      api.get<Workload[]>('/people/workload'),
      api.get<MonthView>('/month', { month }),
      api.get<TodayView>('/today'),
      api.get<AuditRow[]>('/audit', { limit: 40 }),
      api.get<HistoryMonth[]>('/history', { months: 6 }),
      api.get<Assignment[]>('/assignments')
    ]).then((results) => {
      if (cancelled) return;

      const [b, c, cl, p, w, m, t, a, h, as] = results;
      if (as.status === 'fulfilled') setAssignments(as.value);
      if (h.status === 'fulfilled') setHistory(h.value);
      if (b.status === 'fulfilled') setBoard(b.value);
      if (c.status === 'fulfilled') setPlan(c.value);
      if (cl.status === 'fulfilled') setWireClients(cl.value);
      if (p.status === 'fulfilled') setPeople(p.value);
      if (w.status === 'fulfilled') setLoads(w.value);
      if (m.status === 'fulfilled') setMonthView(m.value);
      if (t.status === 'fulfilled') setTodayView(t.value);
      if (a.status === 'fulfilled') setAudit(a.value);

      // Only the board failing is worth saying out loud — it is the read every
      // screen depends on. The rest degrade to an empty section.
      const reason = b.status === 'rejected' ? b.reason : null;
      setError(reason === null ? null : reason instanceof Error ? reason.message : String(reason));
      setLoading(false);
      shown.current = view;
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, currentUser?.id, currentUser?.linkedPersonaId, month, nonce]);

  /* ---------------- clients ---------------- */

  const allClients = useMemo<Client[]>(() => {
    /*
     * Who is on each client: the people assigned to it, lead first, then
     * anybody else whose name is on one of its pieces.
     *
     * This used to be only the second half, from before clients had a team —
     * so a client with Johan as its lead showed nobody on the Clients page
     * until he happened to own a piece on the board, and the client page, the
     * team page and the export each disagreed with the assignments set in
     * Admin. The assignments are the answer; piece owners are added so that
     * somebody helping out on one reel is still visible.
     */
    const ownersByClient = new Map<string, Set<string>>();
    const rank: Record<string, number> = { lead: 0, member: 1 };
    for (const a of [...assignments].sort((x, y) => (rank[x.role] ?? 2) - (rank[y.role] ?? 2))) {
      // Somebody who can only look is not on the work.
      if (a.role === 'view') continue;
      const set = ownersByClient.get(a.client_id) ?? new Set<string>();
      set.add(a.person_id);
      ownersByClient.set(a.client_id, set);
    }
    const touchedByClient = new Map<string, number>();
    for (const col of board?.columns ?? []) {
      for (const card of col.cards) {
        if (card.owner_id !== null) {
          const set = ownersByClient.get(card.client_id) ?? new Set<string>();
          set.add(card.owner_id);
          ownersByClient.set(card.client_id, set);
        }
        // How long the freshest piece has sat still, which is the closest
        // thing the board has to "when did anything last happen here".
        const days = card.days_in_stage;
        const best = touchedByClient.get(card.client_id);
        if (best === undefined || days < best) touchedByClient.set(card.client_id, days);
      }
    }

    return wireClients.map((c) => {
        const line = monthView?.clients.find((m) => m.client_id === c.id);
        return {
          id: c.id,
          name: c.name,
          initials: c.initials ?? deriveInitials(c.name),
          color: c.avatar_color,
          industry: c.category ?? 'No category',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          accountStatus: ACCOUNT_STATUS[c.status] ?? 'Active',
          engagement: c.engagement === 'one_off' ? 'One-off' : 'Recurring',
          activeProjects: line?.cycle_id == null ? 0 : 1,
          completedDeliverables: line?.delivered ?? 0,
          pendingDeliverables: Math.max((line?.committed ?? 0) - (line?.delivered ?? 0), 0),
          assignedTeamIds: [...(ownersByClient.get(c.id) ?? [])],
          lastActivity: lastTouched(touchedByClient.get(c.id)),
          // Spread rather than assigned, so that "not a partner" stays an
          // absent key all the way to the component that decides whether to
          // draw a money column. A null would draw the column and fill it
          // with a dash, which reads as "nothing agreed" rather than "not
          // yours to see".
          ...(c.retainer_amount === undefined || c.retainer_amount === null
            ? {}
            : {
                monthlyRetainer: c.retainer_amount,
                retainerCurrency: c.retainer_currency ?? 'INR'
              }),
          ...(c.archived_at === null ? {} : { archivedAt: c.archived_at })
        };
      });
  }, [wireClients, monthView, board, assignments]);

  // Fetched together and split here, so the archive tab costs no request of
  // its own and nothing else in the platform ever sees an archived client.
  const clients = useMemo(() => allClients.filter((c) => c.archivedAt === undefined), [allClients]);
  const archivedClients = useMemo(() => allClients.filter((c) => c.archivedAt !== undefined), [allClients]);

  /* ---------------- people ---------------- */

  const everyone = useMemo<TeamMember[]>(() => {
    const load = new Map(loads.map((w) => [w.person_id, w]));
    const openByOwner = new Map<string, { open: number; done: number }>();
    for (const col of board?.columns ?? []) {
      for (const card of col.cards) {
        if (card.owner_id === null) continue;
        const bucket = openByOwner.get(card.owner_id) ?? { open: 0, done: 0 };
        if (card.stage === 'posted') bucket.done += 1;
        else bucket.open += 1;
        openByOwner.set(card.owner_id, bucket);
      }
    }

    return people.map((p) => {
      const w = load.get(p.id);
      const photo = p.avatar_at === null ? undefined : photoUrl(p.id, p.avatar_at);
      const counts = openByOwner.get(p.id) ?? { open: 0, done: 0 };
      const capacity = p.capacity ?? 0;
      // The clients they are on, which is what a person means by "my clients".
      const onClients = assignments.filter((a) => a.person_id === p.id).length;
      return {
        id: p.id,
        name: p.name,
        email: '',
        role:
          p.role === 'partner'
            ? 'Admin'
            : p.role === 'manager'
              ? 'Manager'
              : p.role === 'viewer'
                ? 'Viewer'
                : 'Team Member',
        department: p.title ?? (p.role === 'partner' ? 'Partners' : 'Studio'),
        title: p.title ?? (p.role === 'partner' ? 'Partner' : 'Studio'),
        ...(photo === undefined ? {} : { photo }),
        color: personColor(p.id),
        status: p.status === 'active' ? 'Active' : 'Inactive',
        activeTasks: counts.open,
        completedTasks: counts.done,
        // The design's card had an Active cell and a Pending cell, and here
        // they are the same number — a piece that is open is a piece that is
        // pending. The card shows one of them and the clients count instead.
        pendingTasks: counts.open,
        clients: onClients,
        // Drawn from the number on the card, not from the server's own flag on
        // `/people/workload`. The two count the same table and agree for
        // anybody active, but that row does not exist for an archived person,
        // and a card reading "11 clients" with no mark against it — or worse,
        // "0" with one — is the kind of small lie that stops people trusting
        // the screen. The line itself is `MAX_CLIENTS` in `src/db/people.ts`.
        tooManyClients: onClients > MAX_CLIENTS,
        // Deliberately not clamped at 100. Somebody carrying 130% of what they
        // said they could is the single thing this number exists to surface,
        // and a bar that stops at full hides exactly that person.
        workload: capacity === 0 ? 0 : Math.round(((w?.committed ?? 0) / capacity) * 100),
        performance:
          counts.open + counts.done === 0
            ? 0
            : Math.round((counts.done / (counts.open + counts.done)) * 100),
        projects: [],
        joined: ''
      } satisfies TeamMember;
    });
  }, [people, loads, board, assignments]);

  const team = useMemo(() => everyone.filter((m) => m.status === 'Active'), [everyone]);
  const archivedTeam = useMemo(() => everyone.filter((m) => m.status !== 'Active'), [everyone]);

  const photos = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const m of everyone) {
      if (m.photo === undefined) continue;
      byKey.set(m.id, m.photo);
      byKey.set(m.name.toLowerCase(), m.photo);
    }
    return byKey;
  }, [everyone]);
  const photoFor = useCallback(
    (idOrName: string) => photos.get(idOrName) ?? photos.get(idOrName.toLowerCase()),
    [photos]
  );

  const currentClients = useMemo(
    () => clients.filter((c) => c.accountStatus !== 'Former' && c.accountStatus !== 'Lost'),
    [clients]
  );

  // Everybody, including people who have left: a piece they owned still
  // needs their name on it.
  const directory = useMemo<DirectoryEntry[]>(
    () =>
      everyone.map((m) => ({
        id: m.id,
        name: m.name,
        title: m.title,
        color: m.color,
        department: m.department
      })),
    [everyone]
  );

  /* ---------------- pieces, as tasks ---------------- */

  const tasks = useMemo<Task[]>(
    () =>
      (board?.columns ?? []).flatMap((col) =>
        col.cards.map(
          (card): Task => ({
            id: card.piece_id,
            title: card.title,
            description: '',
            clientId: card.client_id,
            projectId: card.commitment_id ?? '',
            assigneeId: card.owner_id ?? '',
            // A piece with no deadline sorts and renders as one dated far
            // out rather than crashing every `new Date()` on the page. The
            // screens that care show "no deadline" from `dueDate === ''`.
            dueDate: card.due ?? '',
            priority: PRIORITY_TO_LABEL[card.priority] ?? 'Medium',
            status: STAGE_TO_STATUS[card.stage],
            tags: card.tags,
            createdAt: '',
            weight: card.weight,
            notes: pieceNotes[card.piece_id] ?? []
          })
        )
      ),
    [board, pieceNotes]
  );

  /* ---------------- the same pieces, as a plan ---------------- */

  const content = useMemo<ContentItem[]>(
    () =>
      (plan?.pieces ?? []).map(
        (p): ContentItem => ({
          id: p.piece_id,
          date: p.scheduled_for ?? p.due ?? '',
          clientId: p.client_id,
          cycleMonth: p.cycle_month ?? month,
          monthPinned: p.month_pinned === true,
          commitmentId: p.commitment_id ?? null,
          commitment: p.commitment ?? null,
          platform: PLATFORM_TO_LABEL[p.platform ?? ''] ?? 'Instagram',
          contentType: KIND_TO_LABEL[p.kind] ?? 'Reel',
          title: p.title,
          topic: p.topic ?? '',
          caption: p.caption ?? '',
          hook: p.hook ?? '',
          cta: p.cta ?? '',
          status: STAGE_TO_STATUS[p.stage],
          assignedToId: p.owner_id ?? '',
          designStatus: STEP_TO_LABEL[p.design_status] ?? 'Not Started',
          editingStatus: STEP_TO_LABEL[p.editing_status] ?? 'Not Started',
          approvalStatus: APPROVAL_TO_LABEL[p.approval_status] ?? 'Pending',
          // Derived rather than stored: a piece is posted when it is posted,
          // and a second column saying so is a second column that can be
          // wrong. `scheduled` with a date is scheduled; anything else is not.
          postingStatus:
            p.stage === 'posted'
              ? 'Posted'
              : p.scheduled_for === null
                ? 'Not Scheduled'
                : 'Scheduled',
          postedDate: p.posted_at,
          reelLink: p.reel_link ?? '',
          notes: p.notes ?? ''
        })
      ),
    [plan, month]
  );
  const requirements = useMemo(() => plan?.requirements ?? [], [plan]);

  /* ---------------- cycles, as projects ---------------- */

  const projects = useMemo<Project[]>(
    () =>
      (monthView?.clients ?? [])
        .filter((c) => c.cycle_id !== null)
        .map((c): Project => {
          const target = Math.max(c.committed, c.delivered + c.in_flight, 1);
          return {
            id: c.cycle_id!,
            name: `${c.client_name} — ${new Date(`${month}T00:00`).toLocaleDateString(undefined, { month: 'long' })}`,
            clientId: c.client_id,
            status: c.closed_at !== null ? 'Completed' : c.committed === 0 ? 'Planning' : 'Active',
            progress: Math.round((c.delivered / target) * 100),
            dueDate: endOfMonth(month),
            teamIds: [],
            budgetUsed: 0,
            description: ''
          };
        }),
    [monthView, month]
  );

  const deliverables = useMemo<Deliverable[]>(
    () =>
      (monthView?.clients ?? []).map((c): Deliverable => {
        const outstanding = Math.max(c.committed - c.delivered, 0);
        return {
          id: c.cycle_id ?? `no-cycle-${c.client_id}`,
          clientId: c.client_id,
          name: c.committed === 0 ? 'Nothing committed' : `${c.committed} this month`,
          type: 'Commitment',
          dueDate: endOfMonth(month),
          status:
            c.committed > 0 && outstanding === 0
              ? 'Completed'
              : c.with_client > 0
                ? 'In Review'
                : c.delivered > 0 || c.in_flight > 0
                  ? 'In Progress'
                  : 'Not Started',
          assignedToId: '',
          completionDate: null,
          target: c.committed,
          delivered: c.delivered,
          inFlight: c.in_flight
        };
      }),
    [monthView, month]
  );

  /* ---------------- the feed ---------------- */

  const activityLog = useMemo<ActivityLogEntry[]>(
    () =>
      audit.map((row) => ({
        id: row.id,
        actorId: row.actor_id ?? '',
        action: row.action.replace(/[._]/g, ' '),
        target: row.subject_id ?? row.subject_type,
        time: relativeTime(row.at)
      })),
    [audit]
  );

  const weeklyTime = useMemo<WeeklyTimeEntry[]>(
    () =>
      loads.map((w) => ({
        teamMemberId: w.person_id,
        hoursLogged: w.committed,
        capacityHours: w.capacity ?? 0
      })),
    [loads]
  );

  /**
   * Notifications, derived from what is actually true right now.
   *
   * Every id is a fact about a row rather than a random string, so the same
   * overdue piece produces the same id on every load — which is what makes
   * dismissing one stick, and what makes a piece that gets finished stop
   * notifying rather than sitting in a table marked unread forever.
   */
  const notifications = useMemo<Notification[]>(() => {
    if (todayView === null) return [];
    const out: Notification[] = [];
    // Each kind can be switched off in Settings → Notifications. Absent means on.
    const wants = currentUser?.preferences.notifications ?? {};

    for (const a of wants.overdue === false ? [] : todayView.overdue) {
      out.push({
        id: `overdue:${a.piece_id}:${a.days_late ?? 0}`,
        type: 'overdue',
        message: `${a.title} for ${a.client_name} is ${a.days_late ?? 0} ${a.days_late === 1 ? 'day' : 'days'} late.`,
        time: 'now',
        read: false,
        href: `/clients/${a.client_id}`
      });
    }
    for (const a of wants.feedback === false ? [] : todayView.asked_for) {
      out.push({
        id: `asked:${a.feedback_id}`,
        type: 'approval',
        message: `${a.client_name} asked for a change on ${a.piece_title}.`,
        time: `${a.days_waiting} ${a.days_waiting === 1 ? 'day' : 'days'} ago`,
        read: false,
        href: `/clients/${a.client_id}`
      });
    }
    for (const a of wants.waiting === false ? [] : todayView.awaiting_client) {
      out.push({
        id: `waiting:${a.piece_id}:${a.days_waiting ?? 0}`,
        type: 'task',
        message: `${a.client_name} has had ${a.title} for ${a.days_waiting ?? 0} ${a.days_waiting === 1 ? 'day' : 'days'}.`,
        time: 'now',
        read: false,
        href: `/clients/${a.client_id}`
      });
    }

    return out.map((n) => ({ ...n, read: dismissed.has(n.id) }));
  }, [todayView, dismissed, currentUser?.preferences.notifications]);

  /* ---------------- writes ---------------- */

  /**
   * Move a piece, and handle the one refusal that has an answer.
   *
   * Marking something posted that the client never approved is refused once.
   * That is deliberate and it is not a permission: the point is not to stop
   * anybody — plenty of posts go out on the studio's own judgement — but to
   * make the decision an act somebody took rather than a step nobody noticed.
   * So the dialog offers the override, and taking it writes `anyway` into the
   * audit log, which is what the month recap counts as "went out unapproved".
   *
   * A hard block here would last a fortnight before somebody asked for it to
   * be removed, and then there would be no record at all.
   */
  const moveStage = useCallback(async (id: string, stage: string): Promise<void> => {
    const stagePath = `/pieces/${encodeURIComponent(id)}/stage`;
    // Said here rather than at the four screens that can move a piece, because
    // this is the one place all four go through.
    const felt = stage === 'posted' ? DID['posted'] : stage === 'with_client' ? DID['sentToClient'] : undefined;
    try {
      await api.patch(stagePath, { stage });
      if (felt !== undefined) cheer(felt);
    } catch (err) {
      if (!(err instanceof ApiError) || err.details['reason'] !== 'needs_signoff') throw err;
      const title = typeof err.details['title'] === 'string' ? err.details['title'] : 'This piece';
      const ok = await new Promise<boolean>((answer) => {
        setGate({ title: `Post "${title}" without sign-off?`, question: err.message, answer });
      });
      setGate(null);
      if (!ok) return;
      await api.patch(stagePath, { stage, anyway: true });
      // Deliberately not the happy pose. Something went out that nobody signed
      // off, which is allowed and is not a thing to be cheered.
      cheer({ pose: 'push' });
    }
  }, []);

  const value = useMemo<AppDataContextValue>(() => {
    const dismiss = (ids: string[]) => {
      setDismissed((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        writeDismissed(next);
        return next;
      });
    };

    return {
      loading,
      error,
      month,
      setMonth,
      reload,

      tasks,
      loadTaskNotes: async (id) => {
        const rows = await api.get<WireNote[]>(`/pieces/${encodeURIComponent(id)}/notes`);
        setPieceNotes((prev) => ({ ...prev, [id]: rows.map(toTaskNote) }));
      },
      addTaskNote: async (id, text) => {
        const row = await api.post<WireNote>(`/pieces/${encodeURIComponent(id)}/notes`, { body: text });
        setPieceNotes((prev) => ({ ...prev, [id]: [...(prev[id] ?? []), toTaskNote(row)] }));
      },
      setTaskStatus: async (id, status) => {
        await moveStage(id, STATUS_TO_STAGE[status]);
        reload();
      },
      updateTask: async (id, patch) => {
        const body: Record<string, unknown> = {};
        if (patch.title !== undefined) body['title'] = patch.title;
        if (patch.dueDate !== undefined) body['due'] = patch.dueDate === '' ? null : patch.dueDate;
        if (patch.priority !== undefined) body['priority'] = LABEL_TO_PRIORITY[patch.priority];
        if (patch.tags !== undefined) body['tags'] = patch.tags;
        if (patch.weight !== undefined) body['weight'] = patch.weight;
        if (patch.description !== undefined) body['notes'] = patch.description;

        // The owner is not a field on this route: assigning is its own act,
        // audited as its own act, because "who is this on" is the question
        // somebody asks a week later and a generic patch would bury it.
        if (patch.assigneeId !== undefined) {
          await api.patch(`/pieces/${encodeURIComponent(id)}/owner`, {
            ownerId: patch.assigneeId === '' ? null : patch.assigneeId
          });
        }
        if (Object.keys(body).length > 0) {
          await api.patch(`/pieces/${encodeURIComponent(id)}`, body);
        }
        if (patch.status !== undefined) {
          await moveStage(id, STATUS_TO_STAGE[patch.status]);
        }
        reload();
      },
      addTask: async (task) => {
        await api.post(`/clients/${encodeURIComponent(task.clientId)}/pieces`, {
          title: task.title ?? 'Untitled',
          ...(task.dueDate !== undefined && task.dueDate !== '' ? { due: task.dueDate } : {}),
          ...(task.assigneeId !== undefined && task.assigneeId !== ''
            ? { ownerId: task.assigneeId }
            : {}),
          ...(task.priority !== undefined ? { priority: LABEL_TO_PRIORITY[task.priority] } : {}),
          ...(task.tags !== undefined ? { tags: task.tags } : {}),
          ...(task.weight !== undefined ? { weight: task.weight } : {}),
          month
        });
        cheer(DID['taskAdded']!);
        reload();
      },

      content,
      requirements,
      updateContent: async (id, patch) => {
        const body: Record<string, unknown> = {};
        // Which requirement it counts towards; blank is "none of them".
        if (patch.commitmentId !== undefined) body['commitmentId'] = patch.commitmentId === '' ? null : patch.commitmentId;
        if (patch.title !== undefined) body['title'] = patch.title;
        if (patch.topic !== undefined) body['topic'] = patch.topic;
        if (patch.hook !== undefined) body['hook'] = patch.hook;
        if (patch.caption !== undefined) body['caption'] = patch.caption;
        if (patch.cta !== undefined) body['cta'] = patch.cta;
        if (patch.reelLink !== undefined) body['reelLink'] = patch.reelLink;
        if (patch.notes !== undefined) body['notes'] = patch.notes;
        if (patch.date !== undefined) body['scheduledFor'] = patch.date === '' ? null : patch.date;
        if (patch.platform !== undefined) body['platform'] = LABEL_TO_PLATFORM[patch.platform];
        if (patch.contentType !== undefined) body['kind'] = LABEL_TO_KIND[patch.contentType];
        if (patch.designStatus !== undefined) {
          body['designStatus'] = LABEL_TO_STEP[patch.designStatus];
        }
        if (patch.editingStatus !== undefined) {
          body['editingStatus'] = LABEL_TO_STEP[patch.editingStatus];
        }
        if (patch.approvalStatus !== undefined) {
          body['approvalStatus'] = LABEL_TO_APPROVAL[patch.approvalStatus];
        }

        let moved: Moved | null = null;
        if (Object.keys(body).length > 0) {
          // The server moves a piece to the month its new post date lands in
          // and says so, rather than letting the two drift apart in silence.
          const done = await api.patch<{ moved_from?: string; moved_to?: string }>(
            `/pieces/${encodeURIComponent(id)}`,
            body
          );
          if (done.moved_from !== undefined && done.moved_to !== undefined) {
            moved = { from: done.moved_from, to: done.moved_to };
          }
        }
        if (patch.status !== undefined) {
          await moveStage(id, STATUS_TO_STAGE[patch.status]);
        }
        if (patch.assignedToId !== undefined) {
          await api.patch(`/pieces/${encodeURIComponent(id)}/owner`, {
            ownerId: patch.assignedToId === '' ? null : patch.assignedToId
          });
        }
        reload();
        return moved;
      },
      refileContent: async (id, to) => {
        await api.patch(`/pieces/${encodeURIComponent(id)}/month`, { month: to });
        reload();
      },
      closeMonth: async (clientId, at, carry) => {
        const done = await api.post<Settled>(
          `/clients/${encodeURIComponent(clientId)}/months/${encodeURIComponent(at)}/close`,
          { carry }
        );
        cheer(DID['monthClosed']!);
        reload();
        return done;
      },
      reopenMonth: async (clientId, at) => {
        await api.delete(`/clients/${encodeURIComponent(clientId)}/months/${encodeURIComponent(at)}/close`);
        reload();
      },
      addContent: async (item) => {
        /*
         * Everything the form collected, not the three fields this used to
         * send. `pieces.add` ignored the rest until it was widened to take
         * them — an unread key in a JSON body is not an error — so a person
         * who chose a platform, a format and an owner got a piece back with
         * none of them set, and typed it all again in the spreadsheet.
         *
         * Empty strings are dropped rather than sent. The form starts most of
         * these blank and a blank one means "not said", which is not the same
         * request as setting a field to nothing.
         */
        const created = await api.post<{ id: string | number }>(`/clients/${encodeURIComponent(item.clientId)}/pieces`, {
          title: item.title ?? 'Untitled',
          ...(item.date !== undefined && item.date !== '' ? { scheduledFor: item.date } : {}),
          ...(item.platform !== undefined
            ? { platform: LABEL_TO_PLATFORM[item.platform] }
            : {}),
          ...(item.contentType !== undefined ? { kind: LABEL_TO_KIND[item.contentType] } : {}),
          ...(item.topic !== undefined && item.topic !== '' ? { topic: item.topic } : {}),
          ...(item.assignedToId !== undefined && item.assignedToId !== ''
            ? { ownerId: item.assignedToId }
            : {}),
          ...(item.notes !== undefined && item.notes !== '' ? { notes: item.notes } : {}),
          month
        });
        reload();
        return String(created.id);
      },
      removeContent: async (id) => {
        await api.delete(`/pieces/${encodeURIComponent(id)}`);
        reload();
      },
      restoreContent: async (id) => {
        await api.post(`/pieces/${encodeURIComponent(id)}/restore`, {});
        reload();
      },

      deliverables,
      toggleDeliverable: async () => {
        // A commitment is not a checkbox. It is satisfied by pieces reaching
        // `posted`, counted by the server, and a control that could tick it by
        // hand would let the monthly report disagree with the work. The
        // components that call this render read-only progress instead.
      },

      team,
      archivedTeam,
      archivePerson: async (id) => {
        await api.patch(`/people/${encodeURIComponent(id)}`, { status: 'inactive' });
        reload();
      },
      restorePerson: async (id) => {
        await api.patch(`/people/${encodeURIComponent(id)}`, { status: 'active' });
        reload();
      },
      directory,
      photoFor,
      updateMe: async (patch) => {
        await api.patch('/me', patch);
        reload();
      },
      setPhoto: async (personId, dataUrl) => {
        await api.post(`/people/${encodeURIComponent(personId)}/photo`, { image: dataUrl });
        reload();
      },
      clearPhoto: async (personId) => {
        await api.delete(`/people/${encodeURIComponent(personId)}/photo`);
        reload();
      },
      assignments,
      assignPerson: async (clientId, personId, role) => {
        await api.post(`/clients/${encodeURIComponent(clientId)}/team`, { personId, role });
        reload();
      },
      unassignPerson: async (clientId, personId) => {
        await api.delete(`/clients/${encodeURIComponent(clientId)}/team/${encodeURIComponent(personId)}`);
        reload();
      },
      loadPlan: (clientId) => api.get<PlanLine[]>(`/clients/${encodeURIComponent(clientId)}/plan`),
      savePlan: async (clientId, items) => {
        const out = await api.post<{ plan: PlanLine[] }>(`/clients/${encodeURIComponent(clientId)}/plan`, {
          items
        });
        reload();
        return out.plan;
      },
      currentClients,

      clients,
      archivedClients,
      archiveClient: async (id) => {
        await api.post(`/clients/${encodeURIComponent(id)}/archive`, {});
        reload();
      },
      restoreClient: async (id) => {
        const was = wireClients.find((c) => c.id === id);
        await api.post(`/clients/${encodeURIComponent(id)}/restore`, {});
        // Archiving turns active into former; bringing somebody back is
        // starting work with them again, so it turns it back. A lost prospect
        // restored stays lost until somebody says otherwise.
        if (was?.status === 'former') {
          await api.patch(`/clients/${encodeURIComponent(id)}`, { status: 'active' });
        }
        reload();
      },
      addClient: async (client) => {
        await api.post('/clients', {
          name: client.name ?? '',
          status: (client.accountStatus ?? 'Active').toLowerCase(),
          engagement: client.engagement === 'One-off' ? 'one_off' : 'recurring',
          ...(client.industry !== undefined && client.industry !== ''
            ? { category: client.industry }
            : {}),
          ...(client.monthlyRetainer !== undefined
            ? { retainerAmount: String(client.monthlyRetainer) }
            : {})
        });
        reload();
      },
      updateClient: async (id, patch) => {
        const body: Record<string, unknown> = {};
        if (patch.name !== undefined) body['name'] = patch.name;
        if (patch.industry !== undefined) body['category'] = patch.industry;
        if (patch.accountStatus !== undefined) body['status'] = patch.accountStatus.toLowerCase();
        if (patch.engagement !== undefined) {
          body['engagement'] = patch.engagement === 'One-off' ? 'one_off' : 'recurring';
        }
        if (patch.color !== undefined) body['avatarColor'] = patch.color;
        if (patch.monthlyRetainer !== undefined) {
          body['retainerAmount'] = String(patch.monthlyRetainer);
        }
        await api.patch(`/clients/${encodeURIComponent(id)}`, body);
        reload();
      },
      projects,

      activityLog,
      weeklyTime,
      today: todayView,
      monthView,
      history,

      notifications,
      markNotificationRead: (id) => dismiss([id]),
      markAllNotificationsRead: () => dismiss(notifications.map((n) => n.id))
    };
  }, [
    loading,
    error,
    month,
    reload,
    tasks,
    content,
    requirements,
    deliverables,
    team,
    archivedTeam,
    directory,
    photoFor,
    assignments,
    currentClients,
    clients,
    archivedClients,
    projects,
    activityLog,
    weeklyTime,
    todayView,
    monthView,
    history,
    notifications,
    moveStage
  ]);

  return (
    <AppDataContext.Provider value={value}>
      <PhotoContext.Provider value={photoFor}>{children}</PhotoContext.Provider>
      <ConfirmDialog
        open={gate !== null}
        onClose={() => gate?.answer(false)}
        onConfirm={() => gate?.answer(true)}
        title={gate?.title ?? ""}
        description={gate?.question ?? ""}
        confirmLabel="Post it anyway"
        danger
      />
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (ctx === null) throw new Error('useAppData must be used within AppDataProvider');
  return ctx;
}

/** One month of `GET /history`. */
export interface HistoryMonth {
  month: string;
  posted: number;
  promised: number;
}

/** One note under a piece, as `GET /pieces/:id/notes` sends it. */
interface WireNote {
  id: string;
  body: string;
  created_at: string;
  created_by: string | null;
  author_name: string | null;
}

function toTaskNote(n: WireNote): TaskNote {
  return {
    id: n.id,
    authorId: n.created_by ?? 'agent',
    // No person means the WhatsApp agent or another service wrote it.
    authorName: n.author_name ?? 'Studio agent',
    text: n.body,
    createdAt: n.created_at
  };
}

/** The last day of a `YYYY-MM-01`, which is when a month's work is due by. */
function endOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return isoDate(new Date(y ?? 1970, m ?? 1, 0));
}

/**
 * "2 days ago", from how long the freshest piece has been sitting.
 *
 * Not an audit lookup: the client list would need one query per row for a
 * column people glance at. Undefined means the client has no pieces on the
 * board at all, which is a different thing from nothing having happened
 * recently, and reads as an empty cell rather than "never".
 */
function lastTouched(days: number | undefined): string {
  if (days === undefined) return '';
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
